import {
  deleteModelCategorySuccess,
  selectCustomerConfig,
  showSuccessAlert,
  upsertBulkCategories,
  upsertBulkCategoriesFailure,
  upsertBulkCategoriesRequest,
  upsertBulkCategoriesSuccess,
} from 'actions';
import {
  createCategoriesuccess,
  createCategory,
  createCategoryFailure,
  createCategoryFulfill,
  createCategoryRequest,
  createCategoryRule,
  deleteCategoriesuccess,
  deleteCategory,
  deleteCategoryFailure,
  deleteCategoryFulfill,
  deleteCategoryOutcomeSuccess,
  deleteCategoryRequest,
  fetchAllCategories,
  fetchAllCategoriesFailure,
  fetchAllCategoriesFulfill,
  fetchAllCategoriesRequest,
  fetchAllCategoriesSuccess,
  fetchFilterCategories,
  fetchSingleCategoriesuccess,
  fetchSingleCategory,
  fetchSingleCategoryFailure,
  fetchSingleCategoryFulfill,
  fetchSingleCategoryRequest,
  upsertCategoriesuccess,
  upsertCategory,
  upsertCategoryFailure,
  upsertCategoryFulfill,
  upsertCategoryRequest,
} from 'actions/categories';
import {
  createRuleGroupFailure,
  fetchRuleCustomers,
  setHighlightCustomerScope,
} from 'actions/ruleGroup';
import { apiClientV2 as LitLingoClientV2 } from 'client';
import { push } from 'connected-react-router';
import { resourceQueryParamName } from 'constants/resourceQueryNames';
import { all, call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import { getCustomerDomain } from 'selectors/auth';
import {
  getRuleCategoriesBulkOutcomes,
  getSelectedCustomerOutcomes,
  getSelectedUseCaseOutcomes,
} from 'selectors/bulkOutcomes';
import { getCategoriesList } from 'selectors/categories';
import { getNavParams, getNavParamsByResource } from 'selectors/nav';
import type { API, APIV2, Category, RouteParams, RuleHead, RuleRevision, SagaReturn } from 'types';
import { reverse } from 'utils/urls';
import { v4 } from 'uuid';

function* fetchSingleCategoriesaga(action: ReturnType<typeof fetchSingleCategory>): SagaReturn {
  const { payload } = action;
  yield put(fetchSingleCategoryRequest());
  const response = (yield call(
    [LitLingoClientV2.resources.categories, 'retrieve'],
    payload.categoryId,
    {
      params: {
        include_pii: 'true',
        relationships: ['rule', 'customer'],
      },
    }
  )) as API.Response<APIV2.Categories.Retrieve>;
  if (response.error != null) {
    yield put(fetchSingleCategoryFailure(response.error));
  } else {
    yield put(fetchSingleCategoriesuccess(response.data));
  }
  yield put(fetchSingleCategoryFulfill());
}

function* fetchCategoriesList(paramsArg: Partial<RouteParams>): SagaReturn {
  yield put(fetchAllCategoriesRequest());
  const resourceParams = (yield select(
    getNavParamsByResource(resourceQueryParamName.category)
  )) as ReturnType<ReturnType<typeof getNavParamsByResource>>;
  const params = { ...paramsArg, include_count: true, ...resourceParams };

  const response = (yield call([LitLingoClientV2.resources.categories, 'list'], {
    params,
  })) as API.Response<APIV2.Categories.List>;
  if (response.error != null) {
    yield put(fetchAllCategoriesFailure(response.error));
  } else {
    yield put(fetchAllCategoriesSuccess(response.data));
  }
  yield put(fetchAllCategoriesFulfill());
}

function* fetchAllCategoriesSaga({ payload }: ReturnType<typeof fetchAllCategories>): SagaReturn {
  const navParams = (yield select(getNavParams)) as RouteParams;
  const params = {
    ...navParams,
    include_pii: 'true',
    relationships: ['updated_by', 'rule', 'customer', 'model', 'outcome'],
    ...payload,
  };

  yield call(fetchCategoriesList, params);
}

function* fetchFilterCategoriesSaga({
  payload,
}: ReturnType<typeof fetchFilterCategories>): SagaReturn {
  const params = {
    include_pii: 'true',
    selectable_fields: [
      'uuid',
      'rule.rule_group_uuid',
      'model_uuid',
      'model',
      'rule_revision_uuid',
      'rule_head.rule_group',
      'tenant_name',
    ],
    get_active: 'true',
    any_active: 'true',
    include_staging: 'true', // TODO: only if super admin
    limit: '-1',
    ...payload,
  };

  yield call(fetchCategoriesList, params);
}

function* createCategoriesaga({ payload }: ReturnType<typeof createCategory>): SagaReturn {
  const {
    modelId,
    ruleId,
    outcomeId,
    customerId,
    redirect,
    fromRule,
    scope,
    branch = 'production',
  } = payload;

  yield put(createCategoryRequest());

  const ruleResponse = (yield call([LitLingoClientV2.resources.ruleGroups.extras, 'getRevisions'], {
    params: {
      rule_uuids: [ruleId],
      include_pii: true,
      order_by: 'created_at',
      order_desc: 'true',
      heads_only: 'true',
      relationships: ['heads'],
    },
  })) as API.Response<APIV2.RuleGroups.GetRevisions>;

  if (ruleResponse.error != null) {
    yield put(createCategoryFailure(ruleResponse.error));
  } else {
    const heads = ruleResponse.data.records[0].heads as RuleHead[];

    const stagingHead = heads.find((h) => h.name === 'staging');
    const prodHead = heads.find((h) => h.name === 'production');
    const stagingRule = ruleResponse.data.records.find(
      (r) => r.uuid === stagingHead?.rule_revision_uuid
    );
    const prodRule = ruleResponse.data.records.find((r) => r.uuid === prodHead?.rule_revision_uuid);

    let rule: RuleRevision | null = null;

    if (stagingRule && !prodRule) {
      rule = stagingRule;
    }
    if (!stagingRule && prodRule) {
      rule = prodRule;
    }

    if (stagingRule && prodRule) {
      if (branch === 'staging') {
        rule = stagingRule;
      }
      if (branch === 'production') {
        rule = prodRule;
      }
    }

    if (!rule) return;

    const categoryData = {
      model_uuid: modelId,
      rule_revision_uuid: rule.uuid,
      customer_uuid: customerId,
      outcome_uuid: outcomeId,
      config: {},
    };

    const response = (yield call([LitLingoClientV2.resources.categories, 'upsert'], {
      data: categoryData,
      params: { relationships: ['customer', 'outcome', 'rule_head'] },
    })) as API.Response<APIV2.Categories.Upsert>;

    if (response.error != null) {
      yield put(createCategoryFailure(response.error));
    } else {
      yield put(createCategoriesuccess(response.data));
      if (fromRule) {
        yield put(fetchRuleCustomers(response.data.rule_revision_uuid));
      }
      if (scope) {
        yield put(selectCustomerConfig(customerId));
        yield put(setHighlightCustomerScope(customerId));
      }
      if (redirect) {
        let customerDomain = 'global';
        if (!redirect.includes('global')) {
          customerDomain =
            ((yield select(getCustomerDomain)) as ReturnType<typeof getCustomerDomain>) || '';
        }

        const path = reverse({
          routeName: redirect,
          routeParams: { categoryId: response.data.uuid },
          customerDomain,
        });
        yield put(push(path));
      }
    }
    yield put(createCategoryFulfill());
  }
}

function* createCategoryRuleSaga({ payload }: ReturnType<typeof createCategoryRule>): SagaReturn {
  const { modelId, outcomeId, customerId, redirect, fromRule, scope, ruleName } = payload;

  yield put(createCategoryRequest());

  const ruleGroupResponse = (yield call([LitLingoClientV2.resources.ruleGroups, 'upsert'], {
    data: { name: ruleName ?? 'New Rule' },
  })) as API.Response<APIV2.RuleGroups.Upsert>;
  if (ruleGroupResponse.error != null) {
    yield put(createRuleGroupFailure(ruleGroupResponse.error));
  } else {
    const ruleRevisionResponse = (yield call(
      [LitLingoClientV2.resources.ruleGroups.extras, 'upsertRuleRevision'],
      {
        urlParams: { ruleId: ruleGroupResponse.data.uuid },
        data: {
          name: ruleName ?? 'New Rule',
          description: '',
          config: {
            operands: [],
            operator: 'AND',
            uuid: v4(),
          },
        },
      }
    )) as API.Response<APIV2.RuleGroups.UpsertRuleRevision>;
    if (ruleRevisionResponse.error != null) {
      yield put(createCategoryFailure(ruleRevisionResponse.error));
    } else {
      const categoryData = {
        model_uuid: modelId,
        rule_revision_uuid: ruleRevisionResponse.data.uuid,
        customer_uuid: customerId,
        outcome_uuid: outcomeId,
        config: {},
      };

      const response = (yield call([LitLingoClientV2.resources.categories, 'upsert'], {
        data: categoryData,
        params: { relationships: ['customer', 'outcome'] },
      })) as API.Response<APIV2.Categories.Upsert>;

      if (response.error != null) {
        yield put(createCategoryFailure(response.error));
      } else {
        yield put(createCategoriesuccess(response.data));
        if (fromRule) {
          yield put(fetchRuleCustomers(response.data.rule_revision_uuid));
        }
        if (scope) {
          yield put(selectCustomerConfig(customerId));
          yield put(setHighlightCustomerScope(customerId));
        }
        if (redirect) {
          let customerDomain = 'global';
          if (!redirect.includes('global')) {
            customerDomain =
              ((yield select(getCustomerDomain)) as ReturnType<typeof getCustomerDomain>) || '';
          }

          const path = reverse({
            routeName: redirect,
            routeParams: { ruleId: ruleGroupResponse.data.uuid },
            customerDomain,
          });
          yield put(push(path));
        }
      }
      yield put(createCategoryFulfill());
    }
  }
}

function* deleteCategoriesaga(action: ReturnType<typeof deleteCategory>): SagaReturn {
  const { payload } = action;
  const { id, ruleId, modelId, outcome } = payload;
  yield put(deleteCategoryRequest(id));

  const response = (yield call(
    [LitLingoClientV2.resources.categories, 'delete'],
    id
  )) as API.Response<APIV2.Categories.Delete>;

  if (response.error != null) {
    yield put(deleteCategoryFailure(response.error));
  } else {
    yield put(deleteCategoriesuccess(id));
    if (ruleId) {
      yield put(fetchRuleCustomers(ruleId));
    }
    if (modelId) {
      yield put(deleteModelCategorySuccess({ categoryId: id, id: modelId }));
      yield put(showSuccessAlert('Deleted Model'));
    }
    if (outcome) {
      yield put(deleteCategoryOutcomeSuccess({ id }));
      yield put(showSuccessAlert('Deleted Outcome'));
    }
    // yield put(prepareUndeleteAlert({ resource: 'categories', id, fetchAction: fetchAllCategories }));
  }
  yield put(deleteCategoryFulfill());
}

function* upsertCategoriesaga(action: ReturnType<typeof upsertCategory>): SagaReturn {
  const { payload } = action;

  yield put(upsertCategoryRequest());

  const response = (yield call([LitLingoClientV2.resources.categories, 'upsert'], {
    data: payload,
    params: { relationships: ['customer', 'updated_by', 'outcome', 'rule', 'model'] },
  })) as API.Response<APIV2.Categories.Upsert>;
  if (response.error != null) {
    yield put(upsertCategoryFailure(response.error));
  } else {
    yield put(showSuccessAlert('Saved Category'));
    yield put(upsertCategoriesuccess(response.data));

    if (payload.redirect) {
      const customerDomain = 'global';
      const path = reverse({
        routeName: 'global-category-manager',
        routeParams: { categoryId: response.data.uuid },
        customerDomain,
      });
      yield put(push(path));
    }
  }

  yield put(upsertCategoryFulfill());
}

function* upsertBulkCategoriesSaga(): SagaReturn {
  yield put(upsertBulkCategoriesRequest());

  const ruleCategories = (yield select(getRuleCategoriesBulkOutcomes)) as ReturnType<
    typeof getRuleCategoriesBulkOutcomes
  >;
  const model = (yield select(getSelectedUseCaseOutcomes)) as ReturnType<
    typeof getSelectedCustomerOutcomes
  >;
  const customer = (yield select(getSelectedCustomerOutcomes)) as ReturnType<
    typeof getSelectedCustomerOutcomes
  >;
  const categories = (yield select(getCategoriesList)) as ReturnType<typeof getCategoriesList>;

  const data = {
    model_uuid: model?.uuid || '',
    target_customer_uuid: customer?.uuid || '',
    categories: Object.values(ruleCategories).reduce<Category[]>(
      (acc, value) => [...acc, ...value.categories.map((c) => c)],
      []
    ),
  };

  const response = (yield call([LitLingoClientV2.resources.categories.extras, 'bulkPut'], {
    data,
    params: { relationships: ['customer', 'updated_by', 'outcome', 'rule', 'model'] },
  })) as API.Response<APIV2.Categories.Upsert>;

  const categoriesToDelete = categories.filter(
    (c) => customer?.uuid === c.customer_uuid && !data.categories.find((cat) => cat.uuid === c.uuid)
  );

  yield all(
    categoriesToDelete.map((cat) =>
      call(deleteCategoriesaga, {
        payload: {
          id: cat.uuid,
        },
        type: deleteCategory.toString(),
      })
    )
  );

  if (response.error != null) {
    yield put(upsertBulkCategoriesFailure(response.error));
  } else {
    yield put(showSuccessAlert('Saved Categories'));
    yield put(upsertBulkCategoriesSuccess());
  }

  yield put(upsertCategoryFulfill());
}

function* categoriesSaga(): SagaReturn {
  yield takeLatest(fetchAllCategories.toString(), fetchAllCategoriesSaga);
  yield takeLatest(fetchFilterCategories.toString(), fetchFilterCategoriesSaga);
  yield takeLatest(upsertCategory.toString(), upsertCategoriesaga);
  yield takeLatest(upsertBulkCategories.toString(), upsertBulkCategoriesSaga);
  yield takeLatest(fetchSingleCategory.toString(), fetchSingleCategoriesaga);
  yield takeLatest(createCategory.toString(), createCategoriesaga);
  yield takeLatest(createCategoryRule.toString(), createCategoryRuleSaga);
  yield takeEvery(deleteCategory.toString(), deleteCategoriesaga);
}

export default categoriesSaga;
